import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Field,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  useFileUpload,
} from '@chakra-ui/react';
import { LuArchive, LuArchiveRestore, LuTrash2, LuUpload } from 'react-icons/lu';
import { useNavigate, useParams } from 'react-router-dom';
import {
  archiveProject,
  deleteProjectLogo,
  getProject,
  getProjectSettings,
  projectLogoUrl,
  unarchiveProject,
  updateProject,
  updateProjectSettings,
  uploadProjectLogo,
  type ImputationOrder,
  type Project,
} from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useMe } from '@/lib/MeContext';
import { toaster } from '@/components/ui/toaster';
import {
  FileUploadHiddenInput,
  FileUploadList,
  FileUploadRootProvider,
  FileUploadTrigger,
} from '@/components/ui/file-upload';
import { ProjectForm, type ProjectFormValues } from './ProjectForm';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export function ProjectEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { refresh } = useProject();
  const { me } = useMe();
  const isAdmin = me?.role === 'admin';
  const [project, setProject] = useState<Project | null>(null);
  const [imputationOrder, setImputationOrder] = useState<ImputationOrder>('interest_first');
  const [adminFee, setAdminFee] = useState('0.00');
  const [moraPercent, setMoraPercent] = useState('0');
  const [graceDays, setGraceDays] = useState('0');
  const [waterRight, setWaterRight] = useState('3000.00');
  const [brandColor, setBrandColor] = useState('');
  const [receiptFooter, setReceiptFooter] = useState('');
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDeleting, setLogoDeleting] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const logoUpload = useFileUpload({
    maxFiles: 1,
    accept: ['image/png', 'image/jpeg'],
    maxFileSize: MAX_LOGO_BYTES,
    onFileReject: (details) => {
      const codes = new Set(details.files.flatMap((f) => f.errors));
      if (codes.has('FILE_TOO_LARGE')) {
        toaster.create({ title: 'Archivo demasiado grande', description: 'Máximo 2 MB.', type: 'error' });
      } else if (codes.has('FILE_INVALID_TYPE')) {
        toaster.create({ title: 'Formato no permitido', description: 'Usa PNG o JPG.', type: 'error' });
      } else {
        toaster.create({ title: 'Archivo no válido', type: 'error' });
      }
    },
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    getProject(id)
      .then(setProject)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'No se pudo cargar'))
      .finally(() => setLoading(false));
  }, [id]);

  const loadSettings = useCallback(async () => {
    if (!id) return;
    try {
      const s = await getProjectSettings(id);
      setImputationOrder(s.imputationOrder);
      setAdminFee(s.defaultAdminFee);
      setMoraPercent((Number(s.defaultMoraMonthlyRate) * 100).toString());
      setGraceDays(String(s.defaultMoraGraceDays));
      setWaterRight(s.defaultWaterRight);
      setBrandColor(s.brandColor ?? '');
      setReceiptFooter(s.receiptFooter ?? '');
      setHasLogo(s.hasLogo);
    } catch {
      // Settings are best-effort here; the rest of the page still functions.
    }
  }, [id]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  async function saveImputation(next: ImputationOrder) {
    if (!id) return;
    const prev = imputationOrder;
    setImputationOrder(next);
    try {
      await updateProjectSettings(id, { imputationOrder: next });
      toaster.create({ title: 'Política de imputación actualizada', type: 'success' });
    } catch (err) {
      setImputationOrder(prev);
      toaster.create({
        title: 'No se pudo actualizar',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    }
  }

  async function saveFeeMora() {
    if (!id) return;
    const fee = (Number(adminFee) || 0).toFixed(2);
    const rateFraction = ((Number(moraPercent) || 0) / 100).toFixed(6);
    const grace = Math.max(0, Math.trunc(Number(graceDays) || 0));
    const water = (Number(waterRight) || 0).toFixed(2);
    try {
      await updateProjectSettings(id, {
        defaultAdminFee: fee,
        defaultMoraMonthlyRate: rateFraction,
        defaultMoraGraceDays: grace,
        defaultWaterRight: water,
      });
      // Reflect the normalized/persisted values so the form matches a reload.
      setAdminFee(fee);
      setMoraPercent((Number(rateFraction) * 100).toString());
      setGraceDays(String(grace));
      setWaterRight(water);
      toaster.create({ title: 'Valores de mora y costo administrativo guardados', type: 'success' });
    } catch (err) {
      toaster.create({
        title: 'No se pudo guardar',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    }
  }

  async function onUploadLogo() {
    if (!id) return;
    const file = logoUpload.acceptedFiles[0];
    if (!file) {
      toaster.create({ title: 'Selecciona un archivo', type: 'error' });
      return;
    }
    setLogoUploading(true);
    try {
      await uploadProjectLogo(id, file);
      logoUpload.clearFiles();
      setHasLogo(true);
      setLogoVersion((v) => v + 1);
      toaster.create({ title: 'Logo actualizado', type: 'success' });
    } catch (err) {
      toaster.create({
        title: 'No se pudo subir el logo',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setLogoUploading(false);
    }
  }

  async function onDeleteLogo() {
    if (!id) return;
    setLogoDeleting(true);
    try {
      await deleteProjectLogo(id);
      setHasLogo(false);
      setLogoVersion((v) => v + 1);
      toaster.create({ title: 'Logo eliminado', type: 'success' });
    } catch (err) {
      toaster.create({
        title: 'No se pudo eliminar el logo',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setLogoDeleting(false);
    }
  }

  async function saveBrand() {
    if (!id) return;
    setBrandSaving(true);
    try {
      await updateProjectSettings(id, {
        brandColor: brandColor || null,
        receiptFooter: receiptFooter || null,
      });
      toaster.create({ title: 'Marca y recibo actualizados', type: 'success' });
    } catch (err) {
      toaster.create({
        title: 'No se pudo guardar',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setBrandSaving(false);
    }
  }

  async function handleSubmit(values: ProjectFormValues) {
    if (!id) return;
    setFormError(null);
    setSubmitting(true);
    try {
      await updateProject(id, {
        name: values.name,
        description: values.description.trim() || null,
        address: values.address.trim() || null,
        countryCode: values.countryCode,
        departmentId: values.departmentId || null,
        municipalityId: values.municipalityId || null,
      });
      toaster.create({ title: 'Proyecto actualizado', type: 'success' });
      await refresh();
      navigate('/proyectos');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo guardar';
      setFormError(msg);
      toaster.create({ title: 'No se pudo guardar', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchiveToggle() {
    if (!project) return;
    setBusy(true);
    try {
      if (project.status === 'active') {
        await archiveProject(project.id);
        toaster.create({ title: 'Proyecto archivado', type: 'success' });
      } else {
        await unarchiveProject(project.id);
        toaster.create({ title: 'Proyecto restaurado', type: 'success' });
      }
      await refresh();
      navigate('/proyectos');
    } catch (err) {
      toaster.create({
        title: 'Operación fallida',
        description: err instanceof Error ? err.message : '',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <HStack gap="3" color="fg.muted">
          <Spinner size="sm" />
          <Text fontSize="sm">Cargando…</Text>
        </HStack>
      </Box>
    );
  }

  if (loadError || !project) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Stack gap="4" maxW="3xl">
          <Text color="red.600" fontSize="sm">
            {loadError ?? 'Proyecto no encontrado'}
          </Text>
          <Box>
            <Button variant="outline" onClick={() => navigate('/proyectos')}>
              Volver
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  }

  const initial: ProjectFormValues = {
    code: project.code,
    name: project.name,
    description: project.description ?? '',
    address: project.address ?? '',
    countryCode: project.countryCode,
    departmentId: project.departmentId ?? '',
    municipalityId: project.municipalityId ?? '',
  };

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="3xl">
        <Stack gap="1">
          <Text
            fontSize="xs"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="0.06em"
            fontWeight="700"
          >
            Administración · Proyectos
          </Text>
          <HStack gap="3" align="center" wrap="wrap">
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              {project.name}
            </Heading>
            <Badge colorPalette={project.status === 'active' ? 'green' : 'gray'}>
              {project.status === 'active' ? 'Activo' : 'Archivado'}
            </Badge>
          </HStack>
          <Text color="fg.muted">Código {project.code}</Text>
        </Stack>

        <ProjectForm
          initial={initial}
          lockCode
          submitLabel="Guardar cambios"
          submitting={submitting}
          error={formError}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/proyectos')}
        />

        {isAdmin && (
          <>
            <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
              <Heading size="sm" mb="2">
                Política de imputación de pagos
              </Heading>
              <Text color="fg.muted" fontSize="sm" mb="4">
                Define cómo se aplican los pagos a las cuotas de los contratos de este proyecto.
              </Text>
              <Field.Root maxW="sm">
                <Field.Label>Orden de imputación</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={imputationOrder}
                    onChange={(e) => saveImputation(e.currentTarget.value as ImputationOrder)}
                  >
                    <option value="interest_first">Interés → Capital</option>
                    <option value="principal_first">Capital → Interés</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <Field.HelperText>Aplica a los pagos registrados en este proyecto.</Field.HelperText>
              </Field.Root>
            </Box>

            <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
              <Heading size="sm" mb="2">Mora y costo administrativo</Heading>
              <Text color="fg.muted" fontSize="sm" mb="4">
                Valores por defecto para nuevos contratos. Mora y costo administrativo aplican solo
                a contratos financiados con interés; el derecho de agua aplica a todos.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
                <Field.Root>
                  <Field.Label>Costo administrativo (Q/mes)</Field.Label>
                  <Input type="number" min="0" step="0.01" value={adminFee} onChange={(e) => setAdminFee(e.currentTarget.value)} />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Tasa de mora (%/mes)</Field.Label>
                  <Input type="number" min="0" step="0.01" value={moraPercent} onChange={(e) => setMoraPercent(e.currentTarget.value)} />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Días de gracia</Field.Label>
                  <Input type="number" min="0" step="1" value={graceDays} onChange={(e) => setGraceDays(e.currentTarget.value)} />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Derecho de agua (Q)</Field.Label>
                  <Input type="number" min="0" step="0.01" value={waterRight} onChange={(e) => setWaterRight(e.currentTarget.value)} />
                </Field.Root>
              </SimpleGrid>
              <Button mt="4" size="sm" colorPalette="brand" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }} onClick={saveFeeMora}>
                Guardar
              </Button>
            </Box>

            <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
              <Heading size="sm" mb="2">Marca / Recibo</Heading>
              <Text color="fg.muted" fontSize="sm" mb="4">
                Logo, color y pie de página que se muestran en los recibos de pago de este proyecto.
              </Text>
              <Stack gap="6">
                <Field.Root>
                  <Field.Label>Logo del proyecto</Field.Label>
                  <Stack gap="3">
                    {hasLogo && (
                      <img
                        src={`${projectLogoUrl(project.id)}?v=${logoVersion}`}
                        alt="Logo del proyecto"
                        style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }}
                      />
                    )}
                    <FileUploadRootProvider value={logoUpload}>
                      <FileUploadHiddenInput />
                      <Stack direction={{ base: 'column', sm: 'row' }} gap="2" align={{ base: 'stretch', sm: 'center' }}>
                        <FileUploadTrigger asChild>
                          <Button size="sm" variant="outline" flexShrink={0}>
                            <Icon as={LuUpload} /> Seleccionar archivo
                          </Button>
                        </FileUploadTrigger>
                        <Button size="sm" onClick={onUploadLogo} loading={logoUploading} flexShrink={0}>
                          Subir logo
                        </Button>
                        {hasLogo && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorPalette="red"
                            onClick={onDeleteLogo}
                            loading={logoDeleting}
                            flexShrink={0}
                          >
                            <Icon as={LuTrash2} /> Eliminar logo
                          </Button>
                        )}
                      </Stack>
                      <FileUploadList showSize clearable />
                    </FileUploadRootProvider>
                  </Stack>
                  <Field.HelperText>PNG o JPG, máximo 2 MB.</Field.HelperText>
                </Field.Root>

                <Field.Root maxW="xs">
                  <Field.Label>Color de marca</Field.Label>
                  <HStack gap="3">
                    <Input
                      type="color"
                      value={brandColor || '#1a56db'}
                      onChange={(e) => setBrandColor(e.currentTarget.value)}
                      p="1"
                      w="14"
                      h="10"
                      flexShrink={0}
                      cursor="pointer"
                    />
                    <Input
                      value={brandColor}
                      onChange={(e) => setBrandColor(e.currentTarget.value)}
                      placeholder="#1a56db"
                    />
                  </HStack>
                  <Field.HelperText>
                    Color de acento usado en el recibo. Déjalo vacío para usar el predeterminado.
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>Pie de página del recibo</Field.Label>
                  <Textarea
                    value={receiptFooter}
                    onChange={(e) => setReceiptFooter(e.currentTarget.value)}
                    placeholder="Ej. Gracias por su pago. Dudas o consultas: contacto@empresa.com"
                    rows={3}
                    maxLength={600}
                  />
                  <Field.HelperText>{receiptFooter.length}/600 caracteres.</Field.HelperText>
                </Field.Root>

                <Box>
                  <Button
                    size="sm"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                    onClick={saveBrand}
                    loading={brandSaving}
                  >
                    Guardar
                  </Button>
                </Box>
              </Stack>
            </Box>
          </>
        )}

        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
          <Heading size="sm" mb="2">
            {project.status === 'active' ? 'Archivar proyecto' : 'Restaurar proyecto'}
          </Heading>
          <Text color="fg.muted" fontSize="sm" mb="4">
            {project.status === 'active'
              ? 'El proyecto deja de aparecer en el selector y en la lista activa. Sus datos y contratos se conservan y puede restaurarse en cualquier momento.'
              : 'El proyecto vuelve a estar disponible en el selector y en la lista activa.'}
          </Text>
          <Button
            variant="outline"
            colorPalette={project.status === 'active' ? 'gray' : 'green'}
            onClick={handleArchiveToggle}
            loading={busy}
            w={{ base: 'full', md: 'auto' }}
          >
            <Icon as={project.status === 'active' ? LuArchive : LuArchiveRestore} />
            {project.status === 'active' ? 'Archivar' : 'Restaurar'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
